import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { checkPaymentStatus } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solaro-founding/artifacts/6pe0ovti_Solaro%20Capri%20Gin%20-%20Horizontal%20Vectorial%20Handpainted%20PNG%20TRANSPARENT.png";

export default function ConfirmationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [confirmedBottles, setConfirmedBottles] = useState([]);
  const sessionId = searchParams.get("session_id");

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    if (attempts >= 8) {
      setStatus("timeout");
      return;
    }
    try {
      const res = await checkPaymentStatus(sessionId);
      if (res.data.payment_status === "paid") {
        setStatus("success");
        setConfirmedBottles(res.data.bottle_numbers || []);
        fetchUser();
        return;
      }
      if (res.data.status === "expired") {
        setStatus("expired");
        return;
      }
      // Still processing
      setTimeout(() => pollStatus(attempts + 1), 2500);
    } catch {
      if (attempts < 5) {
        setTimeout(() => pollStatus(attempts + 1), 2500);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, fetchUser]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="confirmation-loading">
        <div className="text-center animate-fade-in-up">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-6" />
          <h2 className="font-display text-2xl text-[#002366] mb-2">Confirming Your Reservation</h2>
          <p className="font-body text-sm text-[#1A2B4C]/50">Please wait while we verify your payment...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-3 sm:px-4 py-8 sm:py-12" data-testid="confirmation-success">
        <div className="w-full max-w-lg text-center animate-fade-in-up">
          <img src={LOGO_URL} alt="Solaro" className="h-10 sm:h-14 mx-auto mb-6 sm:mb-8 object-contain" />

          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#002366] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Check className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>

          <h1 className="font-display text-2xl sm:text-4xl text-[#002366] mb-3 sm:mb-4 tracking-tight">
            {confirmedBottles.length === 1
              ? `Bottle #${confirmedBottles[0]} Is Yours`
              : `${confirmedBottles.length} Bottles Are Yours`}
          </h1>

          <p className="font-display text-lg text-[#D4AF37] mb-6">
            Welcome to Solaro, {user?.name}
          </p>

          {/* Bottle numbers display */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {confirmedBottles.map((num) => (
              <div
                key={num}
                className="w-16 h-24 bg-[#002366] rounded-t-full rounded-b-md flex items-center justify-center shadow-lg"
              >
                <span className="font-display text-lg text-white font-bold">{num}</span>
              </div>
            ))}
          </div>

          <div className="bg-white p-6 sm:p-8 shadow-sm text-left mb-8">
            <h3 className="font-display text-lg text-[#002366] mb-4">What Happens Next</h3>
            <div className="space-y-4 font-body text-sm text-[#1A2B4C]/70 leading-relaxed">
              <p>
                Your numbered edition of Solaro Capri Gin is confirmed. When the full platform launches, your deposit will be applied to your first order.
              </p>
              <div className="p-4 bg-[#F9F7F2] border border-[#D1CFC7]">
                <h4 className="font-display text-sm text-[#002366] uppercase tracking-wider mb-3">Your Bottle Includes</h4>
                <ul className="space-y-2">
                  <li>Il Versatore — the Solaro glass pourer, part of the serving ritual</li>
                  <li>A signed postcard from Lorenzo</li>
                  <li>Founding member status in the Capri Polo Club</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-[#002366]/5 border border-[#002366]/10">
              <p className="font-body text-sm text-[#002366] italic leading-relaxed">
                "Thank you for being part of the founding 300. Every bottle carries a story — and now, one carries your name. See you on the island."
              </p>
              <p className="font-body text-xs text-[#002366]/60 mt-2">— Lorenzo, Founder</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              data-testid="view-grid-btn"
              onClick={() => navigate("/grid")}
              className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-5"
            >
              Back to the Grid
            </Button>
            <Button
              data-testid="view-reservations-btn"
              onClick={() => navigate("/reservations")}
              variant="outline"
              className="border-[#002366] text-[#002366] hover:bg-[#002366] hover:text-white rounded-none uppercase tracking-widest text-sm px-8 py-5"
            >
              My Reservations
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error / Timeout / Expired states
  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="confirmation-error">
      <div className="text-center max-w-md animate-fade-in-up">
        <h2 className="font-display text-2xl text-[#002366] mb-3">
          {status === "timeout" ? "Payment Processing" : status === "expired" ? "Session Expired" : "Something Went Wrong"}
        </h2>
        <p className="font-body text-sm text-[#1A2B4C]/60 mb-6">
          {status === "timeout"
            ? "Your payment may still be processing. Please check your reservations page."
            : status === "expired"
              ? "This payment session has expired. Please try again."
              : "We couldn't verify your payment. Please check your reservations or try again."}
        </p>
        <div className="flex gap-3 justify-center">
          <Button
            data-testid="back-to-grid-btn"
            onClick={() => navigate("/grid")}
            className="bg-[#002366] text-white hover:bg-[#001133] rounded-none uppercase tracking-widest text-sm px-8 py-5"
          >
            Back to Grid
          </Button>
        </div>
      </div>
    </div>
  );
}
